import { CardContent, CardHeader, Stack } from "@mui/material"
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import MaterialButton from "../../../components/MaterialButton"
import AddIcon from '@mui/icons-material/Add';
import MaterialTypography from "../../../components/MaterialTypography";
import { useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { useEffect, useState } from "react";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { deleteData, fetchData, postData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useSnackbar } from "../../../context/popup";
import UploadFile from "../../Utils/UploadFile";
import { useLoading } from "../../../components/Loading";
import DistrictListFilterDialog from './components/DistrictListFilterDialog.js';


const DistrictList = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();

    const currentPage = useSelector((state) => state?.generalDistrictStore?.currentPage);
    const rowsPerPage = useSelector((state) => state?.generalDistrictStore?.rowsPerPage);
    const totalPages = useSelector((state) => state?.generalDistrictStore?.totalPages);

    const [districtData, setDistrictData] = useState([]);
    const [isRefresh, setIsRefresh] = useState(false);
    const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
    const [fileUploading, setFileUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
    const [search, setSearch] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [filterData, setFilterData] = useState({
        isActive: "",
        stateId: "",
    });
    

    useEffect(() => {
        fetchDistrictList();
    },[isRefresh, currentPage, rowsPerPage, search])

    //List API
    const fetchDistrictList = async () => {
        setCircleLoading(true);
        try {
            let url = `/reference/general/district?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&stateId=${filterData?.stateId}`;
                        
            //for active and inactive status
            if(filterData.isActive !== "") {
                url += `&isActive=${filterData.isActive}`;
            }

            const response = await fetchData(url);
        
        if(response?.success){
            setDistrictData(response?.data?.items);
            dispatch(setTotalPages(response?.data?.totalPages));     
        }
        else{
            //throw error
            showSnackbar("Error Fetching District List", "error")
        }
        } catch (error) {
            console.error('Error Fetching Districts:', error);
            showSnackbar(error?.response?.data?.message || 'Network Error', "error");
        }
        finally{
            setCircleLoading(false);
        }
    };

    //bulk file upload api function
    const handleFileUploadSuccess = async (file) => {
        setFileUploading(true)
        setUploadSuccess(false)

        try {
            const formData = new FormData()

            formData.append('file', file)

            const result = await postData(`/reference/master/bulkUpload/general-district`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });        

            if (result?.success) {
                setUploadSuccess(true)
                showSnackbar(result?.message,"success");
                setOpenUploadFileDialog(false);
                setClearFileOnSuccess(true);
                fetchDistrictList(); 
            }
            else {
                setUploadSuccess(false);
                showSnackbar('Faiiled to upload file', "error")
            }

        } catch (error) {
                const errorMessage = error?.response?.data?.message;                
                console.error('Upload error:', error || errorMessage)
                showSnackbar(errorMessage, "error")
        } 
        finally {
                setFileUploading(false)
        }
    };

    //delete api function
    const handleDelete = async (row) => {
        try {
            const response = await deleteData(`/reference/general/district-delete`, {ids:[row?.id]});
            setIsRefresh(!isRefresh);
            if(response?.success){
                showSnackbar(response?.message, "success");
            }
            else{
                showSnackbar(response?.message, "error");
            }
        } catch (error) {
            console.error("Error deleting data:", error);
            showSnackbar(error?.response?.data?.message, "error");
        }
    };

    //Table Mapping
    const districtListTable = {
        columns: [
            {
            key: "sno",
            label: "S.No.",
            renderCell: (row) => row.sno,
            },
            {
            key: "stateId",
            label: "State",
            renderCell: (row) => row.stateId,
            },
            {
            key: "name",
            label: "District Name",
            renderCell: (row) => row.name,
            },
            {
            key: "displayName",
            label: "Display Name",
            renderCell: (row) => row.displayName,
            },
            {
                key: "isActive",
                label: "Status",
                renderCell: (row) => (
                    <span>
                    {row.isActive ? (
                        <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
                    ) : (
                        <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
                    )}
                    </span>
                )
            },
        ],
        rows: districtData?.map((district, index) => ({
            id: district?.id,
            sno: index + 1,
            stateId: district?.state?.name || "",
            name: district?.name || "",
            displayName: district?.displayName || "",
            isActive: district?.isActive,
        })),
    };

    const handleAddNewDistrict = () => {
        navigate(`/general-master/district-list/district-details`);
    }

    const actions = {
        edit: true,
        view: true,
        delete: true,

        onEdit: (row) => {
            navigate(`/general-master/district-list/district-details?pageType=edit&id=${row.id}`);
        },
        onView: (row) => {
            navigate(`/general-master/district-list/district-details?pageType=view&id=${row.id}`);
        },
        onDelete: (row) => handleDelete(row),
    };

    const handlePaginationChange = (page) => {
        dispatch(setCurrentPage(page));
    };

    const handleRowsPerPageChange = (rowsPerPage) => {
        dispatch(setRowsPerPage(rowsPerPage));
        dispatch(setCurrentPage(1));
    };

    const handleSearch = (value) => {
        setSearch(value);
    };

  return (
    <DashboardLayout>

        <CardHeader title={(
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
            >
                <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                        District List
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                        List of Districts around the world.
                    </MaterialTypography>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <MaterialButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    startIcon={<FilterAlt/>}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    >
                        Filter
                    </MaterialButton>
                    <MaterialButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    startIcon={<FileUpload/>}
                    onClick={() => setOpenUploadFileDialog(true)}>
                        Bulk Upload
                    </MaterialButton>
                    <MaterialButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddNewDistrict()}
                    >
                        District
                    </MaterialButton>
                </Stack>
            </Stack>


        )} 
        />
        <CardContent>
            <Table
            columns={districtListTable?.columns}
            data={districtListTable?.rows}
            actions={actions}
            pagination={{
                totalPages,
                currentPage,
                onPageChange: (page) => handlePaginationChange(page),
                rowsPerPage,
                onRowsPerPageChange: (rowsPerPage) => handleRowsPerPageChange(rowsPerPage),
                rowsPerPageOptions: [5, 10, 15, 20],
            }}
            getRowKey={(row) => row.id}
            className="custom-table"
            noDataLabel="No Districts found"
            handleSearch={handleSearch}
            />            
        </CardContent>

        <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={['csv', 'xlsx']}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
        />
        <DistrictListFilterDialog
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
            setAnchorEl(null);   // close popup
            fetchDistrictList();  // call API after filter applied
        }}
        />

    </DashboardLayout>
  )
}

export default DistrictList